import React, { PropsWithChildren } from 'react'
import { T } from '@transifex/react'
// PRO-130: deep antd/es/* subpaths, NOT the `antd` root barrel. This wrapper
// is on the boot-static graph (AntdI18n renders <Empty/>); importing from
// 'antd' pulls antd/es/index.js — the whole component barrel (Select/Table/
// Tree/DatePicker → rc-select/rc-table/rc-tree/rc-picker) — into the entry
// chunk. The deep subpaths pull only the components this file actually uses.
import AntdEmpty from 'antd/es/empty'
import type { EmptyProps } from 'antd/es/empty'
import Space from 'antd/es/space'
import Typography from 'antd/es/typography'

import {
  DownCircleFilled,
  LeftCircleFilled,
  RightCircleFilled,
  UpCircleFilled,
} from 'src/icons/antd-compat'

import Img from '../images/empty/Exclamation.png'
import List from '../images/empty/List.png'

const EmptyWrapper = ({ children }: PropsWithChildren<unknown>) => {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {children}
    </div>
  )
}

type TEmptyProps = EmptyProps & {
  withoutWrapper?: boolean
}

export const Empty = ({ withoutWrapper = false, ...props }: TEmptyProps) => {
  const component = (
    <AntdEmpty
      description={
        <span>
          <T _str="No data found" />
        </span>
      }
      image={Img}
      imageStyle={{
        height: 50,
      }}
      {...props}
    />
  )
  if (!withoutWrapper) {
    return <EmptyWrapper>{component}</EmptyWrapper>
  }
  return component
}

// Forward antd's static preset images so `Empty.PRESENTED_IMAGE_SIMPLE`
// resolves at runtime and in the type (the plain-function wrapper
// otherwise drops the statics — TS2339 + the simple icon silently
// falling back to the default image).
Empty.PRESENTED_IMAGE_SIMPLE = AntdEmpty.PRESENTED_IMAGE_SIMPLE
Empty.PRESENTED_IMAGE_DEFAULT = AntdEmpty.PRESENTED_IMAGE_DEFAULT

type EmptySelectItemProps = {
  direction?: 'top' | 'left' | 'right' | 'bottom'
  size?: 'default'
}

const sizeMap: Record<
  Exclude<EmptySelectItemProps['size'], undefined>,
  number
> = {
  default: 80,
}

export const EmptySelectItem = ({
  direction = 'left',
  size = 'default',
}: EmptySelectItemProps) => {
  return (
    <AntdEmpty
      image={List}
      imageStyle={{
        height: sizeMap[size],
      }}
      description={
        <Typography.Text type="secondary">
          <Space>
            {direction === 'bottom' && (
              <>
                <DownCircleFilled /> <T _str="Select an item at the bottom" />
              </>
            )}
            {direction === 'left' && (
              <>
                <LeftCircleFilled /> <T _str="Select an item on the left" />
              </>
            )}
            {direction === 'right' && (
              <>
                <RightCircleFilled /> <T _str="Select an item on the right" />
              </>
            )}
            {direction === 'top' && (
              <>
                <UpCircleFilled /> <T _str="Select an item at the top" />
              </>
            )}
          </Space>
        </Typography.Text>
      }
    />
  )
}
